import {
  SdVerticalStackContainerTile,
  TileStatus,
  TileType,
} from '@varo/graphql-schema-types';
import cardAdapterMap from 'features/home/HomeScreen/adapters/adapterMap/cardAdapterMap';
import mapHomeTiles from 'features/home/HomeScreen/adapters/mapHomeTiles/mapHomeTiles';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { CardGroupProps } from 'shared/containers/vxl/groups/CardGroup/CardGroup';
import {
  isChildNodeType,
  VXLChildNode,
  VXLSection,
  VXLSectionId,
} from 'shared/renderer/types';
import { Maybe } from 'shared/utils/types';

interface VXLVerticalStackNode extends VXLSection {
  name: VXLSectionId.CARD_GROUP;
  props: CardGroupProps;
}

/**
 * Extracts the title text from a FullWidthTextComponentTile within the
 * SDVerticalStackContainerTile's child tiles.
 */
const extractTitleFromTiles = (
  tiles: SdVerticalStackContainerTile['tiles'],
): string => {
  const textTile = tiles.find(
    (tile) =>
      tile.tileType === TileType.FullWidthTextComponentTile && 'text' in tile,
  );

  if (textTile && 'text' in textTile) {
    const { text } = textTile as { text?: { copy?: string } };
    return text?.copy ?? '';
  }

  return '';
};

/**
 * Filters out FullWidthTextComponentTile tiles, keeping only renderable
 * child tiles (e.g. CreditScoreTile, VxlTrackerCardTile).
 */
const filterRenderableTiles = (
  tiles: SdVerticalStackContainerTile['tiles'],
) => {
  return tiles.filter(
    (tile) => tile.tileType !== TileType.FullWidthTextComponentTile,
  );
};

const sdVerticalStackContainerTile = (
  tile: SdVerticalStackContainerTile,
  dependencies: HomeCardDependencies,
): Maybe<VXLVerticalStackNode> => {
  if (tile.status !== TileStatus.Loaded) {
    return null;
  }

  const titleText = extractTitleFromTiles(tile.tiles);
  const renderableTiles = filterRenderableTiles(tile.tiles);

  const subTiles: VXLChildNode[] = mapHomeTiles(
    cardAdapterMap,
    renderableTiles,
    dependencies,
  ).filter(isChildNodeType);

  if (subTiles.length === 0) {
    return null;
  }

  return {
    id: tile.id,
    name: VXLSectionId.CARD_GROUP,
    props: {
      titleText,
      components: subTiles,
    },
  };
};

export default sdVerticalStackContainerTile;
